// =============================================================================
// BP-05-03: Calendar Frontend - CalendarView Component
// =============================================================================

import React, { useCallback, useMemo } from 'react';
import { Calendar, dateFnsLocalizer, View, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, Grid3X3 } from 'lucide-react';
import { CalendarView as CalendarViewType, type CalendarEvent, type AppointmentSummary } from '../types';
import { useCalendarStore } from '../stores/calendarStore';
import { AppointmentCard } from './AppointmentCard';
import { cn } from '@/core/lib/utils';

import 'react-big-calendar/lib/css/react-big-calendar.css';

// -----------------------------------------------------------------------------
// Localizer Setup
// -----------------------------------------------------------------------------

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface CalendarViewProps {
  events: CalendarEvent[];
  isLoading?: boolean;
  onEventClick?: (event: CalendarEvent) => void;
  onSlotSelect?: (slot: SlotInfo) => void;
  onEventDrop?: (event: CalendarEvent, start: Date, end: Date) => void;
  className?: string;
}

// -----------------------------------------------------------------------------
// View Mapping
// -----------------------------------------------------------------------------

const viewMapping: Record<CalendarViewType, View> = {
  [CalendarViewType.DAY]: 'day',
  [CalendarViewType.WEEK]: 'week',
  [CalendarViewType.MONTH]: 'month',
  [CalendarViewType.AGENDA]: 'agenda',
};

const reverseViewMapping: Record<View, CalendarViewType> = {
  day: CalendarViewType.DAY,
  week: CalendarViewType.WEEK,
  month: CalendarViewType.MONTH,
  agenda: CalendarViewType.AGENDA,
  work_week: CalendarViewType.WEEK,
};

// -----------------------------------------------------------------------------
// Custom Toolbar
// -----------------------------------------------------------------------------

interface ToolbarProps {
  date: Date;
  view: View;
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
  onView: (view: View) => void;
}

function CustomToolbar({ date, view, onNavigate, onView }: ToolbarProps) {
  const formatDate = () => {
    switch (view) {
      case 'day':
        return format(date, 'EEEE, MMMM d, yyyy');
      case 'week':
        return format(date, 'MMMM yyyy');
      case 'month':
        return format(date, 'MMMM yyyy');
      case 'agenda':
        return format(date, 'MMMM yyyy');
      default:
        return format(date, 'MMMM yyyy');
    }
  };

  return (
    <div className="flex items-center justify-between mb-4 pb-4 border-b border-warm-gray/20">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onNavigate('TODAY')}
          className="px-4 py-2 text-sm font-medium text-navy bg-cream hover:bg-cream-dark rounded-lg transition-colors"
        >
          Today
        </button>
        <div className="flex items-center bg-cream rounded-lg">
          <button
            onClick={() => onNavigate('PREV')}
            className="p-2 hover:bg-cream-dark rounded-l-lg transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-navy" />
          </button>
          <button
            onClick={() => onNavigate('NEXT')}
            className="p-2 hover:bg-cream-dark rounded-r-lg transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-navy" />
          </button>
        </div>
        <h2 className="text-xl font-display font-semibold text-navy ml-2">
          {formatDate()}
        </h2>
      </div>

      <div className="flex items-center gap-1 bg-cream rounded-lg p-1">
        <button
          onClick={() => onView('day')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            view === 'day'
              ? 'bg-sage text-white'
              : 'text-navy hover:bg-cream-dark'
          )}
        >
          <CalendarIcon className="w-4 h-4" />
          Day
        </button>
        <button
          onClick={() => onView('week')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            view === 'week'
              ? 'bg-sage text-white'
              : 'text-navy hover:bg-cream-dark'
          )}
        >
          <Grid3X3 className="w-4 h-4" />
          Week
        </button>
        <button
          onClick={() => onView('month')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            view === 'month'
              ? 'bg-sage text-white'
              : 'text-navy hover:bg-cream-dark'
          )}
        >
          <Grid3X3 className="w-4 h-4" />
          Month
        </button>
        <button
          onClick={() => onView('agenda')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            view === 'agenda'
              ? 'bg-sage text-white'
              : 'text-navy hover:bg-cream-dark'
          )}
        >
          <List className="w-4 h-4" />
          Agenda
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Custom Event Component
// -----------------------------------------------------------------------------

interface EventComponentProps {
  event: CalendarEvent;
}

function EventComponent({ event }: EventComponentProps) {
  const appointment = event.resource as AppointmentSummary;
  const color = appointment.color || '#7A9E7E';

  return (
    <div
      className="h-full px-1.5 py-0.5 text-xs rounded overflow-hidden cursor-pointer"
      style={{
        backgroundColor: `${color}20`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div className="font-medium truncate" style={{ color }}>
        {event.title}
      </div>
      {appointment.providerName && (
        <div className="text-warm-gray truncate text-[10px]">
          {appointment.providerName}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Custom Month Event
// -----------------------------------------------------------------------------

function MonthEventComponent({ event }: EventComponentProps) {
  const appointment = event.resource as AppointmentSummary;
  const color = appointment.color || '#7A9E7E';

  return (
    <div
      className="px-1.5 py-0.5 text-xs rounded cursor-pointer truncate"
      style={{
        backgroundColor: `${color}30`,
        color: color,
      }}
    >
      <span className="font-medium">{format(event.start, 'h:mm a')}</span>
      <span className="ml-1">{event.title}</span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function CalendarViewComponent({
  events,
  isLoading,
  onEventClick,
  onSlotSelect,
  onEventDrop,
  className,
}: CalendarViewProps) {
  const {
    currentView,
    currentDate,
    weekStartsOn,
    setView,
    setCurrentDate,
    showWeekends,
  } = useCalendarStore();

  const view = viewMapping[currentView];

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate);
    },
    [setCurrentDate]
  );

  const handleViewChange = useCallback(
    (newView: View) => {
      setView(reverseViewMapping[newView] || CalendarViewType.MONTH);
    },
    [setView]
  );

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      onEventClick?.(event);
    },
    [onEventClick]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      onSlotSelect?.(slotInfo);
    },
    [onSlotSelect]
  );

  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => {
      const appointment = event.resource as AppointmentSummary;
      const color = appointment.color || '#7A9E7E';

      return {
        style: {
          backgroundColor: `${color}20`,
          borderLeft: `3px solid ${color}`,
          color: color,
          borderRadius: '4px',
        },
      };
    },
    []
  );

  const dayPropGetter = useCallback(
    (date: Date) => {
      const isToday =
        date.toDateString() === new Date().toDateString();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

      return {
        className: cn(
          isToday && 'bg-sage/5',
          isWeekend && !showWeekends && 'opacity-50'
        ),
      };
    },
    [showWeekends]
  );

  const components = useMemo(
    () => ({
      toolbar: CustomToolbar,
      event: EventComponent,
      month: {
        event: MonthEventComponent,
      },
    }),
    []
  );

  const formats = useMemo(
    () => ({
      timeGutterFormat: (date: Date) => format(date, 'h a'),
      eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
      dayHeaderFormat: (date: Date) => format(date, 'EEE M/d'),
      dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`,
      agendaDateFormat: (date: Date) => format(date, 'EEE, MMM d'),
      agendaTimeFormat: (date: Date) => format(date, 'h:mm a'),
      agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
    }),
    []
  );

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center h-96', className)}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  return (
    <div className={cn('bg-white rounded-xl p-4 shadow-soft', className)}>
      <Calendar
        localizer={localizer}
        events={events}
        view={view}
        date={currentDate}
        onNavigate={handleNavigate}
        onView={handleViewChange}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        selectable
        popup
        eventPropGetter={eventStyleGetter}
        dayPropGetter={dayPropGetter}
        components={components}
        formats={formats}
        step={30}
        timeslots={2}
        min={new Date(0, 0, 0, 6, 0, 0)}
        max={new Date(0, 0, 0, 22, 0, 0)}
        style={{ height: 'calc(100vh - 250px)', minHeight: 600 }}
        className="brightpath-calendar"
      />
    </div>
  );
}

export { CalendarViewComponent as CalendarView };


export default CalendarViewComponent;

